import { useEffect, useState } from "react";
import { CalendarDays, Plus, Trash2, Check, Loader2, AlertTriangle, Repeat, X, Edit3, Download, List, Grid3x3, FileText, Wallet } from "lucide-react";
import { api, API_BASE, fmtEur, fmtDate, todayISO } from "../lib/api";
import { toast } from "sonner";

const CATEGORIE = ["F24 / Tasse", "IVA", "INPS", "Affitto", "Fornitori", "Utenze", "Assicurazioni", "Altro"];
const RICORRENZE = [
    { v: "nessuna", l: "Nessuna" },
    { v: "mensile", l: "Mensile" },
    { v: "trimestrale", l: "Trimestrale" },
    { v: "semestrale", l: "Semestrale" },
    { v: "annuale", l: "Annuale" },
];

function daysFromToday(d) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(d);
    due.setHours(0, 0, 0, 0);
    return Math.round((due - today) / 86400000);
}

function CalendarView({ month, setMonth, items, onEdit, onPay }) {
    const [y, m] = month.split("-").map(Number);
    const firstDay = new Date(y, m - 1, 1);
    const daysInMonth = new Date(y, m, 0).getDate();
    // Monday-first: shift so monday = 0
    const startOffset = (firstDay.getDay() + 6) % 7;

    const byDay = {};
    for (const it of items) {
        if (it.data_scadenza?.startsWith(month)) {
            const dayNum = parseInt(it.data_scadenza.slice(8, 10));
            (byDay[dayNum] = byDay[dayNum] || []).push(it);
        }
    }

    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    while (cells.length < 42) cells.push(null);

    const monthLabel = firstDay.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    const todayStr = new Date().toISOString().slice(0, 10);

    function prev() {
        const d = new Date(y, m - 2, 1);
        setMonth(d.toISOString().slice(0, 7));
    }
    function next() {
        const d = new Date(y, m, 1);
        setMonth(d.toISOString().slice(0, 7));
    }

    const weekHeaders = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

    return (
        <section className="card-soft p-6" data-testid="calendar-view">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="font-display text-xl font-semibold text-forest-dark capitalize">{monthLabel}</h2>
                <div className="inline-flex gap-1">
                    <button onClick={prev} className="btn-ghost text-xs px-3 py-1.5" data-testid="cal-prev">←</button>
                    <button onClick={() => setMonth(new Date().toISOString().slice(0, 7))} className="btn-ghost text-xs px-3 py-1.5" data-testid="cal-today">Oggi</button>
                    <button onClick={next} className="btn-ghost text-xs px-3 py-1.5" data-testid="cal-next">→</button>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">
                {weekHeaders.map((w) => <div key={w} className="py-1">{w}</div>)}
            </div>

            <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
                {cells.map((d, idx) => {
                    if (d === null) return <div key={idx} className="aspect-square rounded-lg bg-bone-2/40" />;
                    const dateStr = `${month}-${String(d).padStart(2, "0")}`;
                    const dayItems = byDay[d] || [];
                    const hasUnpaid = dayItems.some((it) => !it.pagato);
                    const hasOverdue = dayItems.some((it) => !it.pagato && dateStr < todayStr);
                    const allPaid = dayItems.length > 0 && dayItems.every((it) => it.pagato);
                    const isToday = dateStr === todayStr;

                    const bg = hasOverdue
                        ? "bg-red-50 border-red-300"
                        : hasUnpaid
                        ? "bg-amber-50 border-amber-300"
                        : allPaid
                        ? "bg-emerald-50 border-emerald-300"
                        : "bg-white border-soft";

                    return (
                        <div
                            key={idx}
                            className={`aspect-square min-h-[64px] rounded-lg border p-1.5 flex flex-col text-xs ${bg} ${isToday ? "ring-2 ring-forest" : ""}`}
                            data-testid={`cal-day-${d}`}
                        >
                            <div className={`font-semibold ${isToday ? "text-forest" : "text-forest-dark"}`}>{d}</div>
                            <div className="flex-1 overflow-y-auto space-y-0.5 mt-0.5">
                                {dayItems.slice(0, 2).map((it) => (
                                    <button
                                        key={it.id}
                                        onClick={() => onEdit(it)}
                                        className={`block w-full text-left truncate px-1 py-0.5 rounded text-[10px] font-medium ${
                                            it.pagato ? "bg-emerald-200/60 text-emerald-900 line-through" :
                                            dateStr < todayStr ? "bg-red-200/70 text-red-900" :
                                            "bg-amber-200/70 text-amber-900"
                                        }`}
                                        title={`${it.titolo}${it.importo ? ' · €' + it.importo : ''}`}
                                        data-testid={`cal-item-${it.id}`}
                                    >
                                        {it.titolo}
                                    </button>
                                ))}
                                {dayItems.length > 2 && (
                                    <div className="text-[9px] text-stone-500 px-1">+ {dayItems.length - 2}</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex flex-wrap gap-3 mt-4 text-[10px] text-stone-500">
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-300" /> In arrivo</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-300" /> Scaduta</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-300" /> Pagata</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm ring-2 ring-forest bg-white" /> Oggi</span>
            </div>
        </section>
    );
}

export default function Scadenze() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("aperte");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [viewMode, setViewMode] = useState("lista"); // "lista" | "calendario"
    const [calMonth, setCalMonth] = useState(new Date().toISOString().slice(0, 7));
    const [docs, setDocs] = useState([]);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        titolo: "",
        descrizione: "",
        data_scadenza: todayISO(),
        importo: "",
        categoria: "Altro",
        ricorrenza: "nessuna",
        notify_days_before: 7,
        document_id: "",
    });

    async function load() {
        setLoading(true);
        try {
            const params = {};
            if (filter === "aperte") params.pagato = false;
            else if (filter === "pagate") params.pagato = true;
            const r = await api.get("/deadlines", { params });
            setItems(r.data);
        } catch {
            toast.error("Errore caricamento");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, [filter]);
    useEffect(() => {
        api.get("/documents").then((r) => setDocs(r.data)).catch(() => null);
    }, []);

    function openNew() {
        setEditing(null);
        setForm({
            titolo: "",
            descrizione: "",
            data_scadenza: todayISO(),
            importo: "",
            categoria: "Altro",
            ricorrenza: "nessuna",
            notify_days_before: 7,
            document_id: "",
        });
        setOpen(true);
    }

    function openEdit(d) {
        setEditing(d);
        setForm({
            titolo: d.titolo,
            descrizione: d.descrizione || "",
            data_scadenza: d.data_scadenza,
            importo: d.importo ?? "",
            categoria: d.categoria,
            ricorrenza: d.ricorrenza,
            notify_days_before: d.notify_days_before,
            document_id: d.document_id || "",
        });
        setOpen(true);
    }

    async function submit() {
        if (!form.titolo.trim()) { toast.error("Titolo obbligatorio"); return; }
        const payload = {
            ...form,
            importo: form.importo === "" ? null : parseFloat(form.importo),
            notify_days_before: parseInt(form.notify_days_before) || 0,
            document_id: form.document_id || null,
        };
        try {
            if (editing) {
                await api.put(`/deadlines/${editing.id}`, payload);
                toast.success("Aggiornata");
            } else {
                await api.post("/deadlines", payload);
                toast.success("Scadenza creata");
            }
            setOpen(false);
            setEditing(null);
            load();
        } catch {
            toast.error("Errore salvataggio");
        }
    }

    async function markPaid(d) {
        try {
            await api.put(`/deadlines/${d.id}`, { pagato: true });
            const msg = d.ricorrenza !== "nessuna"
                ? `Pagata. Ho creato automaticamente la prossima scadenza ${d.ricorrenza}.`
                : "Pagata";
            toast.success(msg);
            load();
        } catch {
            toast.error("Errore");
        }
    }

    async function del(id) {
        if (!confirm("Eliminare la scadenza?")) return;
        await api.delete(`/deadlines/${id}`);
        toast.success("Eliminata");
        load();
    }

    async function openDocPreview(docId) {
        setPreviewLoading(true);
        try {
            const r = await api.get(`/documents/${docId}`);
            setPreviewDoc(r.data);
        } catch {
            toast.error("Documento non trovato");
        } finally {
            setPreviewLoading(false);
        }
    }

    async function quickCreateExpenseFromDoc() {
        if (!previewDoc) return;
        const importoStr = prompt(`Importo della spesa per "${previewDoc.titolo}" (€):`, "");
        if (importoStr === null) return;
        const importo = parseFloat(importoStr.replace(",", "."));
        if (Number.isNaN(importo) || importo <= 0) {
            toast.error("Importo non valido");
            return;
        }
        try {
            await api.post("/expenses", {
                descrizione: previewDoc.titolo,
                importo,
                categoria: previewDoc.categoria === "Contratto fornitore" ? "Acquisto merce" :
                    previewDoc.categoria === "Contratto affitto" ? "Affitto" :
                    previewDoc.categoria === "Assicurazione" ? "Assicurazioni" : "Altro",
                fornitore: "",
                note: `Da documento: ${previewDoc.titolo}`,
                tipo: "spesa",
            });
            toast.success("Spesa creata in Contabilità");
        } catch {
            toast.error("Errore creazione spesa");
        }
    }

    const categorie = Array.from(new Set(items.map((i) => i.categoria))).sort();
    const filteredItems = categoryFilter ? items.filter((i) => i.categoria === categoryFilter) : items;

    return (
        <div className="space-y-6 pb-6" data-testid="scadenze-page">
            <section className="card-soft p-5 flex flex-wrap items-center gap-3 justify-between">
                <div className="flex gap-2" role="tablist">
                    {[
                        { v: "aperte", l: "Da pagare" },
                        { v: "tutte", l: "Tutte" },
                        { v: "pagate", l: "Pagate" },
                    ].map((t) => (
                        <button
                            key={t.v}
                            onClick={() => setFilter(t.v)}
                            data-testid={`filter-${t.v}`}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${filter === t.v ? "bg-forest text-white" : "bg-bone-2 text-stone-600"}`}
                        >
                            {t.l}
                        </button>
                    ))}
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <div className="inline-flex bg-bone-2 rounded-lg p-1" data-testid="view-toggle">
                        <button
                            onClick={() => setViewMode("lista")}
                            className={`px-2.5 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1 transition ${viewMode === "lista" ? "bg-white text-forest shadow-sm" : "text-stone-500"}`}
                            data-testid="view-lista"
                        >
                            <List size={14} /> Lista
                        </button>
                        <button
                            onClick={() => setViewMode("calendario")}
                            className={`px-2.5 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1 transition ${viewMode === "calendario" ? "bg-white text-forest shadow-sm" : "text-stone-500"}`}
                            data-testid="view-calendario"
                        >
                            <Grid3x3 size={14} /> Calendario
                        </button>
                    </div>
                    <a
                        href={`${API_BASE}/exports/deadlines.csv`}
                        download
                        className="btn-ghost inline-flex items-center gap-1 text-xs"
                        data-testid="btn-export-deadlines-csv"
                    >
                        <Download size={14} /> CSV
                    </a>
                    <select
                        className="input-soft max-w-[180px] text-sm py-1.5"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        data-testid="category-filter"
                    >
                        <option value="">Tutte le categorie</option>
                        {categorie.map((c) => <option key={c}>{c}</option>)}
                    </select>
                    <button onClick={openNew} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="btn-new-deadline">
                        <Plus size={16} /> Nuova
                    </button>
                </div>
            </section>

            {viewMode === "calendario" && (
                <CalendarView month={calMonth} setMonth={setCalMonth} items={filteredItems} onEdit={openEdit} onPay={markPaid} />
            )}

            {viewMode === "lista" && (loading ? (
                <div className="card-soft p-6 h-32 animate-pulse" />
            ) : !filteredItems.length ? (
                <section className="card-soft p-10 text-center text-stone-500">
                    <CalendarDays size={36} className="mx-auto opacity-40" />
                    <p className="text-sm mt-2">
                        {filter === "aperte" ? "Nessuna scadenza da pagare. 👏" : "Nessuna scadenza."}
                    </p>
                </section>
            ) : (
                <section className="card-soft p-6">
                    <ul className="divide-y divide-[color:var(--border-soft)] stagger">
                        {filteredItems.map((d) => {
                            const days = daysFromToday(d.data_scadenza);
                            const expired = days < 0 && !d.pagato;
                            const urgent = !d.pagato && days >= 0 && days <= d.notify_days_before;
                            return (
                                <li key={d.id} className="py-4 flex items-start gap-3" data-testid={`deadline-row-${d.id}`}>
                                    <button
                                        onClick={() => markPaid(d)}
                                        disabled={d.pagato}
                                        className={`shrink-0 w-9 h-9 rounded-lg border-2 grid place-items-center transition ${
                                            d.pagato
                                                ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                                                : "bg-white border-stone-200 text-stone-400 hover:border-forest"
                                        }`}
                                        title={d.pagato ? "Pagata" : "Segna come pagata"}
                                        data-testid={`deadline-pay-${d.id}`}
                                    >
                                        <Check size={16} strokeWidth={d.pagato ? 3 : 2} />
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 flex-wrap">
                                            <h3 className={`font-semibold leading-tight ${d.pagato ? "text-stone-400 line-through" : "text-forest-dark"}`}>
                                                {d.titolo}
                                            </h3>
                                            <div className="flex items-center gap-1.5">
                                                {d.ricorrenza !== "nessuna" && (
                                                    <span className="chip bg-bone-2 text-stone-600 text-[10px]">
                                                        <Repeat size={10} /> {d.ricorrenza}
                                                    </span>
                                                )}
                                                <span className="chip bg-forest-50 text-forest text-[10px]">{d.categoria}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                                            <span className={`text-xs ${expired ? "text-red-700 font-semibold" : urgent ? "text-amber-700 font-semibold" : "text-stone-500"}`}>
                                                {expired && <AlertTriangle size={11} className="inline mr-1" />}
                                                {fmtDate(d.data_scadenza)}
                                                {!d.pagato && (
                                                    days === 0 ? " · oggi" :
                                                    days === 1 ? " · domani" :
                                                    days > 0 ? ` · tra ${days} giorni` :
                                                    ` · scaduta da ${-days} giorni`
                                                )}
                                                {d.pagato && d.pagato_data && ` · pagata il ${fmtDate(d.pagato_data)}`}
                                            </span>
                                            {d.importo != null && (
                                                <span className="text-sm font-semibold text-forest">{fmtEur(d.importo)}</span>
                                            )}
                                        </div>
                                        {d.descrizione && <p className="text-xs text-stone-500 mt-1">{d.descrizione}</p>}
                                        {d.document_id && (
                                            <button
                                                onClick={() => openDocPreview(d.document_id)}
                                                className="text-xs text-forest font-semibold inline-flex items-center gap-1 mt-1 hover:underline"
                                                data-testid={`deadline-doc-${d.id}`}
                                            >
                                                <FileText size={11} /> Apri documento collegato
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <button onClick={() => openEdit(d)} className="p-2 text-stone-400 hover:text-forest rounded-lg" data-testid={`deadline-edit-${d.id}`}>
                                            <Edit3 size={14} />
                                        </button>
                                        <button onClick={() => del(d.id)} className="p-2 text-stone-400 hover:text-red-600 rounded-lg" data-testid={`deadline-del-${d.id}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ))}

            {open && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)} data-testid="deadline-modal">
                    <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-soft flex items-center justify-between">
                            <h3 className="font-display text-lg font-semibold text-forest-dark">{editing ? "Modifica scadenza" : "Nuova scadenza"}</h3>
                            <button onClick={() => setOpen(false)} className="p-2 hover:bg-bone-2 rounded-lg"><X size={18} /></button>
                        </div>
                        <div className="p-6 space-y-3">
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Titolo *</span>
                                <input className="input-soft mt-1" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} data-testid="dl-titolo" />
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Data scadenza *</span>
                                    <input type="date" className="input-soft mt-1" value={form.data_scadenza} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} data-testid="dl-data" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Importo €</span>
                                    <input type="number" step="0.01" className="input-soft mt-1" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} data-testid="dl-importo" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Categoria</span>
                                    <select className="input-soft mt-1" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} data-testid="dl-cat">
                                        {CATEGORIE.map((c) => <option key={c}>{c}</option>)}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-xs font-semibold text-stone-600">Ricorrenza</span>
                                    <select className="input-soft mt-1" value={form.ricorrenza} onChange={(e) => setForm({ ...form, ricorrenza: e.target.value })} data-testid="dl-ric">
                                        {RICORRENZE.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                                    </select>
                                </label>
                                <label className="block col-span-2">
                                    <span className="text-xs font-semibold text-stone-600">Avvisami quanti giorni prima</span>
                                    <input type="number" min="0" max="60" className="input-soft mt-1" value={form.notify_days_before} onChange={(e) => setForm({ ...form, notify_days_before: e.target.value })} data-testid="dl-notify" />
                                </label>
                                <label className="block col-span-2">
                                    <span className="text-xs font-semibold text-stone-600">Documento collegato (opzionale)</span>
                                    <select className="input-soft mt-1" value={form.document_id} onChange={(e) => setForm({ ...form, document_id: e.target.value })} data-testid="dl-doc">
                                        <option value="">— Nessun documento —</option>
                                        {docs.map((d) => (
                                            <option key={d.id} value={d.id}>{d.categoria} · {d.titolo}</option>
                                        ))}
                                    </select>
                                    {docs.length === 0 && (
                                        <span className="text-xs text-stone-500 mt-1 inline-block">
                                            Nessun documento in archivio. Caricane uno dalla sezione Documenti.
                                        </span>
                                    )}
                                </label>
                            </div>
                            <label className="block">
                                <span className="text-xs font-semibold text-stone-600">Note</span>
                                <textarea className="input-soft mt-1" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="dl-desc" />
                            </label>
                            <div className="flex gap-2 pt-2">
                                <button onClick={() => setOpen(false)} className="btn-ghost text-sm flex-1" data-testid="dl-cancel">Annulla</button>
                                <button onClick={submit} className="btn-primary text-sm flex-1" data-testid="dl-save">Salva</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {previewDoc && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setPreviewDoc(null)} data-testid="doc-preview-modal">
                    <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-soft flex items-center justify-between sticky top-0 bg-white z-10">
                            <div className="min-w-0">
                                <h3 className="font-display text-lg font-semibold text-forest-dark truncate">{previewDoc.titolo}</h3>
                                <p className="text-xs text-stone-500">
                                    {previewDoc.categoria}
                                    {previewDoc.data_scadenza && ` · Scade ${fmtDate(previewDoc.data_scadenza)}`}
                                </p>
                            </div>
                            <button onClick={() => setPreviewDoc(null)} className="p-2 hover:bg-bone-2 rounded-lg shrink-0">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-6">
                            {previewDoc.mime_type?.startsWith("image/") ? (
                                <img
                                    src={`data:${previewDoc.mime_type};base64,${previewDoc.file_b64}`}
                                    alt={previewDoc.titolo}
                                    className="w-full max-h-[60vh] object-contain rounded-xl bg-bone-2 border border-soft"
                                    data-testid="doc-preview-img"
                                />
                            ) : (
                                <div className="text-center py-8">
                                    <FileText size={48} className="mx-auto text-stone-300" />
                                    <p className="text-sm text-stone-500 mt-2">{previewDoc.nome_file || "File"}</p>
                                    <a
                                        href={`data:${previewDoc.mime_type};base64,${previewDoc.file_b64}`}
                                        download={previewDoc.nome_file || `${previewDoc.titolo}.pdf`}
                                        className="btn-primary mt-4 inline-block text-sm"
                                        data-testid="doc-preview-download"
                                    >
                                        Scarica file
                                    </a>
                                </div>
                            )}
                            {previewDoc.note && (
                                <p className="text-sm text-stone-600 mt-4 bg-bone-2 rounded-xl p-3">{previewDoc.note}</p>
                            )}
                            <div className="mt-5 flex flex-wrap gap-2">
                                <button
                                    onClick={quickCreateExpenseFromDoc}
                                    className="btn-accent text-sm inline-flex items-center gap-2"
                                    data-testid="doc-create-expense"
                                >
                                    <Wallet size={14} /> Crea voce spesa
                                </button>
                                <a
                                    href={`data:${previewDoc.mime_type};base64,${previewDoc.file_b64}`}
                                    download={previewDoc.nome_file || `${previewDoc.titolo}`}
                                    className="btn-ghost text-sm inline-flex items-center gap-2"
                                >
                                    <Download size={14} /> Scarica
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {previewLoading && !previewDoc && (
                <div className="fixed inset-0 bg-black/30 z-50 grid place-items-center" data-testid="doc-preview-loading">
                    <div className="bg-white rounded-xl px-4 py-3 shadow-lg inline-flex items-center gap-2 text-sm">
                        <Loader2 size={16} className="animate-spin" /> Caricamento documento...
                    </div>
                </div>
            )}
        </div>
    );
}
